// ─────────────────────────────────────────────────────────────────────────────
// Shared constants + helpers for the unified Extractor Studio (Schema/Test/History
// workspace + 3-step manual create). Colours come from ./studioTokens — the
// Extractor Studio design-tokens handoff (primary #A04A97, canvas #FAF9FB).
// ─────────────────────────────────────────────────────────────────────────────

// Polls GET /extraction-status/<job_id>/ (ExtractionStatusV2API) every
// `intervalMs` until the job reaches a terminal state. `onTick` (optional)
// is called with each intermediate status payload so callers can surface
// stage text ("queued"/"ingesting"/"extracting") while waiting.
//
// `isCancelled` (optional, () => bool) is checked before each network call
// and again before scheduling the next tick — pass a ref-backed function so
// an unmounted caller (component navigated away / draft switched) stops
// polling instead of driving state updates on a dead component. On the
// backend a *failed job* is reported as an HTTP 500 with {status:"failed",
// error}, not a 200 — axios rejects on that, so it naturally lands in the
// catch below as a normal error (its `.response.data.error` still carries
// the real failure reason; callers using errMsg() already read that key).
export const pollExtractionJob = (getStatusFn, jobId, { intervalMs = 2500, onTick, isCancelled } = {}) =>
  new Promise((resolve, reject) => {
    const cancelled = () => !!(isCancelled && isCancelled());
    const tick = async () => {
      if (cancelled()) { reject(new Error("cancelled")); return; }
      try {
        const s = await getStatusFn(jobId);
        if (cancelled()) { reject(new Error("cancelled")); return; }
        if (s.status === "complete") { resolve(s); return; }
        if (s.status === "failed") { reject(new Error(s.error || "Extraction failed")); return; }
        if (onTick) onTick(s);
        setTimeout(tick, intervalMs);
      } catch (err) { reject(err); }
    };
    tick();
  });

export const STUDIO_DOC_TYPES = [
  { id: "invoice", label: "Invoice" },
  { id: "purchase_order", label: "Purchase Order" },
  { id: "contract", label: "Contract" },
  { id: "receipt", label: "Receipt" },
  { id: "custom", label: "Custom" },
];

// These are the file types accepted by DocumentIngestAPI and the async
// universal-extract-v2 endpoint. AI template generation is intentionally kept
// PDF-only because BuilderStartAPI has a separate PDF-only contract.
export const STUDIO_TEST_FILE_ACCEPT = ".pdf,.docx,.txt,.xlsx,.xls,.rtf,.jpg,.jpeg,.png";
export const STUDIO_TEST_FILE_EXTENSIONS = new Set([
  "pdf", "docx", "txt", "xlsx", "xls", "rtf", "jpg", "jpeg", "png",
]);

export const fileExtension = (file) =>
  String(file?.name || "").split(".").pop()?.toLowerCase() || "";

export const isSupportedStudioTestFile = (file) =>
  !!file && STUDIO_TEST_FILE_EXTENSIONS.has(fileExtension(file));

// The workspace keeps UI-only values on draft fields (enabled, confidence and
// sample_value). ExtractorTemplateSerializer does not accept those properties,
// so direct updates must be projected back to the persisted field contract.
export const serializeTemplateFields = (fields = []) => fields
  .filter((field) => field && field.enabled !== false)
  .map((field, index) => {
    const clean = {
      field_key: String(field.field_key || "").trim(),
      data_type: field.data_type || "text",
      instruction: field.instruction || "",
      output_rules: field.output_rules || "",
      schema_definition: field.schema_definition ?? null,
      field_order: Number(field.field_order) || index + 1,
      grounded_on: field.grounded_on || "",
    };
    if (Number.isInteger(field.id)) clean.id = field.id;
    if (Array.isArray(field.instruction_history)) clean.instruction_history = field.instruction_history;
    if (field.system_mapping !== undefined) clean.system_mapping = field.system_mapping;
    return clean;
  });

export const normalizeTemplateSlug = (value) => String(value || "")
  .trim()
  .toLowerCase()
  .replace(/[^a-z0-9]+/g, "-")
  .replace(/^-+|-+$/g, "")
  .slice(0, 255);

// BuilderFromTemplateAPI intentionally returns a draft projection without DB
// field ids. Reattach ids from the saved template when available so subsequent
// PUTs update existing rows instead of recreating every field.
export const mergeSavedFieldIds = (draftFields = [], savedFields = []) => {
  const byKey = new Map(savedFields.map((field) => [field.field_key, field]));
  return draftFields.map((field) => ({
    ...field,
    ...(byKey.get(field.field_key)?.id ? { id: byKey.get(field.field_key).id } : {}),
  }));
};

// Category badge colours are the handoff's shared set — the same pair is used by
// Library rows, recent cards and the workspace header for a given document type.
export const DOC_TYPE_STYLES = {
  invoice:        { badge: "bg-[#EEF4FD] text-[#2F5FA8]", label: "Invoice" },
  purchase_order: { badge: "bg-[#EEF9F0] text-[#2F7D44]", label: "Purchase Order" },
  contract:       { badge: "bg-[#FDF3E7] text-[#A8662F]", label: "Contract" },
  receipt:        { badge: "bg-[#F5EEFB] text-[#7B3FA8]", label: "Receipt" },
  custom:         { badge: "bg-[#F3F3F3] text-[#6B6B6B]", label: "Custom" },
  other:          { badge: "bg-[#F3F3F3] text-[#6B6B6B]", label: "Other" },
};
export const docTypeStyle = (t) => DOC_TYPE_STYLES[t] || DOC_TYPE_STYLES.custom;

// Field data-type glyph + colour. Reuses the category palette so a schema row and
// a library row never show two different blues for the same idea.
export const FIELD_TYPE_META = {
  text:   { glyph: "T",   badge: "bg-[#EEF4FD] text-[#2F5FA8]" },
  number: { glyph: "#",   badge: "bg-[#EEF9F0] text-[#2F7D44]" },
  date:   { glyph: "D",   badge: "bg-[#FDF3E7] text-[#A8662F]" },
  table:  { glyph: "⊞",   badge: "bg-[#F5EEFB] text-[#7B3FA8]" },
  object: { glyph: "{ }", badge: "bg-[#F3F3F3] text-[#6B6B6B]" },
};
export const fieldTypeMeta = (t) => FIELD_TYPE_META[t] || FIELD_TYPE_META.text;
export const SCHEMA_TYPES = ["text", "number", "date", "table", "object"];

export const CONFIDENCE_STYLES = {
  high:   { badge: "bg-[#E8F3EC] text-[#2F7D44]", label: "HIGH",   tip: "Found clearly and unambiguously. Little review needed." },
  medium: { badge: "bg-[#FBF0E0] text-[#C77A1C]", label: "MEDIUM", tip: "Found, but may be ambiguous or formatted unusually. Worth a quick check." },
  low:    { badge: "bg-[#FBE9E5] text-[#B33A1E]", label: "LOW",    tip: "No value found, or the match is uncertain. Review before relying on it." },
  saved:  { badge: "bg-[#F1EEF0] text-[#9A8E97]", label: "SAVED",  tip: "From the saved template. Not re-tested in this session yet." },
  manual: { badge: "bg-[#F3F3F3] text-[#6B6B6B]", label: "MANUAL", tip: "Added by hand or imported. Not tested against a sample yet." },
};

// A freshly added/imported field carries confidence "medium" from the backend even
// though it was never tested. Per the integration guide, treat any field with no
// sample_value and no grounding as "manual/unconfirmed" for the badge.
export const effectiveConfidence = (field) => {
  const hasValue =
    field.sample_value !== null && field.sample_value !== undefined && field.sample_value !== "" &&
    !(Array.isArray(field.sample_value) && field.sample_value.length === 0);
  if (!hasValue && !field.grounded_on) return "manual";
  return field.confidence || "saved";
};
export const confidenceStyle = (field) => CONFIDENCE_STYLES[effectiveConfidence(field)] || CONFIDENCE_STYLES.saved;

// Engine / retrieval / model options (real backend enums, with short descriptions
// for the manual-create engine step — no fake sample-doc previews).
export const ENGINE_OPTIONS = [
  { value: "standard_layout",   label: "Standard Layout",   recommended: true, desc: "Fast extraction for clean, typed digital PDFs where text is selectable." },
  { value: "advanced_vision",   label: "Advanced Vision",   desc: "AI reads the page directly — best for scanned, photographed or visually rich documents." },
  { value: "form_intelligence", label: "Form Intelligence", desc: "Preserves layout for long, multi-page documents with tables spanning pages." },
  { value: "contextual_ai",     label: "Contextual AI",     desc: "Deeper reasoning for complex or ambiguous documents where context matters." },
];
export const RETRIEVAL_OPTIONS = [
  { value: "full_text",  label: "Full Text" },
  { value: "similarity", label: "Similarity" },
  { value: "fusion",     label: "Hybrid Fusion" },
];
export const MODEL_OPTIONS = [
  { value: "gemini_2_5_flash_lite", label: "Gemini 2.5 Flash Lite (faster)" },
  { value: "gemini_2_5_flash",      label: "Gemini 2.5 Flash (more accurate)" },
];
export const engineLabel = (v) => (ENGINE_OPTIONS.find((e) => e.value === v) || {}).label || v;

export const isEmptyValue = (v) =>
  v === undefined || v === null || v === "" ||
  (Array.isArray(v) && v.length === 0) ||
  (typeof v === "object" && !Array.isArray(v) && Object.keys(v).length === 0);

export const errMsg = (err, fallback) =>
  err?.response?.data?.error || err?.response?.data?.detail || fallback;

export const fileFromFormData = (fd) => { try { return fd.get("file"); } catch { return null; } };

// Parse a pasted template JSON into {valid, error, fields:[{field_key,data_type,instruction}]}.
export const parseImportJSON = (text) => {
  if (!text.trim()) return { valid: false, error: null, fields: [] };
  let obj;
  try { obj = JSON.parse(text); } catch {
    return { valid: false, error: "Invalid JSON — check for missing commas, quotes or brackets.", fields: [] };
  }
  let raw;
  if (Array.isArray(obj)) raw = obj;
  else if (obj && Array.isArray(obj.fields)) raw = obj.fields;
  else return { valid: false, error: 'JSON must be a template object with a "fields" array, or a bare array of fields.', fields: [] };
  if (!raw.length) return { valid: false, error: "No fields found in the JSON.", fields: [] };
  const seen = new Set();
  const fields = [];
  for (const rf of raw) {
    if (!rf || typeof rf.field_key !== "string" || !rf.field_key.trim())
      return { valid: false, error: 'Every field needs a non-empty "field_key" string.', fields: [] };
    if (seen.has(rf.field_key)) return { valid: false, error: "Duplicate field_key values — each field must be unique.", fields: [] };
    seen.add(rf.field_key);
    fields.push({
      field_key: rf.field_key.trim(),
      data_type: String(rf.data_type || "text").toLowerCase(),
      instruction: rf.instruction || "",
    });
  }
  const tables = fields.filter((f) => f.data_type === "table").length;
  return { valid: true, error: null, fields, stats: { count: fields.length, tables } };
};
